import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import {PaginationWrapper} from '../../../Utility/PaginationWrapper';
import {PaginationListType} from '../../../../Types/PaginationParams';
import useSearchFilter from '../../../../Hooks/useSearchFilter';
import SearchFilter from '../../../Utility/SearchFilter';
import ToggleSwitch from "../../../Utility/ToggleSwitch";
import {KnowledgebaseDocumentStatus} from "../../../../Types";

interface PaginatedCardProps<T> {
    title: string;
    itemsPerPage?: number
    listType: PaginationListType;
    isNew: boolean;
    selectedDocumentCount: number;
    renderItem: (item: T, index: number) => React.ReactNode;
    itemsFunction: (
        status: KnowledgebaseDocumentStatus | null,
        page: number,
        itemsPerPage: number,
        optionalParams?: Record<string, string>,
        query?: string | null,
    ) => Promise<{ data: { data: T[]; total: number } }> | any;
}

enum DocumentStatus {
    DONE = "done",
    IN_PROGRESS = "in_progress",
    ERROR = "error",
}

let prevSelectedDocumentCount: number = 0

function PaginatedFileList<T>({
                                  title,
                                  itemsFunction,
                                  renderItem,
                                  listType,
                                  itemsPerPage = 15,
                                  isNew,
                                  selectedDocumentCount
                              }: PaginatedCardProps<T>) {
    const {query, localQuery, searchActions} = useSearchFilter({
        initialQuery: null,
    });
    const i18n = useSelector((state: any) => state.i18n.i18n);

    const [status, setStatus] = useState<KnowledgebaseDocumentStatus | null>(null)

    const itemsFunctionByStatus = useMemo(() => (
        (page: any, itemsPerPage: any, optionalParams: any, query: any) => itemsFunction(status, page, itemsPerPage, optionalParams, query)
    ), [status, itemsFunction])

    useEffect(() => {
        setTimeout(() => {
            if (selectedDocumentCount >= 1 && prevSelectedDocumentCount !== selectedDocumentCount && status !== null) {
                setStatus(null)
                searchActions.clearQuery()
            }
            prevSelectedDocumentCount = selectedDocumentCount
        }, 250)
    }, [selectedDocumentCount, status])

    return (
        <div className={`h-full  max-w-[unset]`}>
            <h4 className="mt-0 mb-3">{title}</h4>
            <div className="w-full flex justify-between">
                <SearchFilter
                    value={localQuery}
                    onChange={searchActions.setLocalQuery}
                    onApply={searchActions.applyQuery}
                    onClear={searchActions.clearQuery}
                    className="mb-3"
                />
                {PaginationListType.KnowledgebaseNewFiles === listType ? <></> :
                    <div>
                        <ToggleSwitch
                            options={[
                                {
                                    label: i18n.translate({id: 'all', sourceName: 'Knowledgebase'}),
                                    function: () => setStatus(null),
                                    active: status == null,
                                },
                                ...(isNew ? [] : [
                                    {
                                        label: i18n.translate({
                                            id: 'document.status.done',
                                            sourceName: 'Knowledgebase'
                                        }),
                                        function: () => setStatus(DocumentStatus.DONE),
                                        active: status === DocumentStatus.DONE,
                                    },
                                    {
                                        label: i18n.translate({
                                            id: 'document.status.in_progress',
                                            sourceName: 'Knowledgebase'
                                        }),
                                        function: () => setStatus(DocumentStatus.IN_PROGRESS),
                                        active: status === DocumentStatus.IN_PROGRESS,
                                    },
                                    {
                                        label: i18n.translate({
                                            id: 'document.status.error',
                                            sourceName: 'Knowledgebase'
                                        }),
                                        function: () => setStatus(DocumentStatus.ERROR),
                                        active: status === DocumentStatus.ERROR,
                                    },
                                ])
                            ]}
                        />
                    </div>}
            </div>

            <PaginationWrapper<T>
                listType={listType}
                itemsFunction={itemsFunctionByStatus}
                itemsPerPage={itemsPerPage}
                query={query ?? null}
                renderItem={renderItem}
            />
        </div>
    );
}

export default PaginatedFileList;
